/** Formats d'affichage des écrans Skipcar+ (montants en centimes, dates ISO, taux en fraction). */

export const euros = (centimes: number | null | undefined, decimales = 2): string =>
  centimes === null || centimes === undefined
    ? 'n.c.'
    : `${(centimes / 100).toLocaleString('fr-FR', {
        minimumFractionDigits: decimales,
        maximumFractionDigits: decimales,
      })} €`;

/** « 2026-10-01 » ou « 2026-10-01 10:00:00 » → « 01/10/2026 ». */
export const dateFr = (iso: string | null | undefined): string =>
  iso ? iso.slice(0, 10).split('-').reverse().join('/') : 'n.c.';

/** 0.0075 → « 0,75 % ». */
export const pourcent = (taux: number): string =>
  `${(taux * 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 3 })} %`;

/** Saisie « 0,75 » (en %) → 0.0075, ou null si la saisie n'est pas un nombre positif. */
export const tauxDepuisSaisie = (saisie: string): number | null => {
  const valeur = Number(saisie.replace(',', '.').replace('%', '').trim());
  return saisie.trim() !== '' && Number.isFinite(valeur) && valeur >= 0 ? Math.round(valeur * 1000) / 100000 : null;
};

export const PHASES = [
  { cle: 'cotation', libelle: 'Cotation' },
  { cle: 'montage', libelle: 'Montage' },
  { cle: 'achat', libelle: 'Achat' },
  { cle: 'portage', libelle: 'Portage' },
  { cle: 'vente', libelle: 'Vente' },
  { cle: 'solde', libelle: 'Solde' },
] as const;

export const LIBELLE_NATURE = {
  qonto: 'Qonto',
  manuel: 'Manuel',
  sans_objet: 'Sans objet',
} as const;

export const LIBELLE_SOURCE_J0 = {
  saisi: 'saisi par Skipcar',
  da: 'date de la déclaration d’achat',
  previsionnel: 'prévisionnel, dossier de conformité',
} as const;

/** Date du jour au format ISO local (YYYY-MM-DD), pour comparer aux dates de l'échéancier. */
export const aujourdhuiIso = (): string => new Date().toLocaleDateString('sv-SE');

export interface BlocDate {
  datePrelevement: string;
}

export interface AvancementContrat<B extends BlocDate> {
  etat: 'avant_j0' | 'portage' | 'terme';
  /** Semaine en cours, comptée comme le moteur (J0+7 = semaine 1), plafonnée à 52. 0 avant J0. */
  semaine: number;
  palier: 1 | 2 | 3 | 4;
  taux: number;
  /** Prochain prélèvement à venir (aujourd'hui compris), null s'il n'y en a plus. */
  prochaine: B | null;
}

const JOUR_MS = 86_400_000;

const palierDeLaSemaine = (semaine: number): 1 | 2 | 3 | 4 => {
  if (semaine <= 4) return 1;
  if (semaine <= 8) return 2;
  if (semaine <= 12) return 3;
  return 4;
};

/** Où en est le contrat : semaine sur 52, palier actif, prochaine échéance. */
export const avancementContrat = <B extends BlocDate>(
  j0: string | null,
  taux: readonly [number, number, number, number],
  echeancier: readonly B[],
  aujourdhui: string,
): AvancementContrat<B> => {
  const jours = j0 === null ? -1 : Math.round((Date.parse(aujourdhui) - Date.parse(j0.slice(0, 10))) / JOUR_MS);
  const semainesEcoulees = Math.max(1, Math.ceil(jours / 7));
  const etat = jours < 0 ? 'avant_j0' : semainesEcoulees > 52 ? 'terme' : 'portage';
  const semaine = etat === 'avant_j0' ? 0 : Math.min(52, semainesEcoulees);
  const palier = palierDeLaSemaine(Math.max(1, semaine));
  const prochaine = etat === 'terme' ? null : (echeancier.find((bloc) => bloc.datePrelevement >= aujourdhui) ?? null);
  return { etat, semaine, palier, taux: taux[palier - 1], prochaine };
};
